import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../state/auth';
import { useLanguage } from '../../i18n/LanguageProvider';
import { Button } from '../Button/Button';
import { InternationalPhoneInput } from '../InternationalPhoneInput/InternationalPhoneInput';
import './profile-phone-field.css';

export function ProfilePhoneField({ value, onVerified, labelPosition = 'inside' }: { value: string; onVerified: (phone: string) => void; labelPosition?: 'inside' | 'above' }) {
  const auth = useAuth();
  const { language } = useLanguage();
  const label = (ru: string, en: string) => language === 'en' ? en : ru;
  const [phone, setPhone] = useState(value);
  const [sentPhone, setSentPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inFlight = useRef(false);
  const phoneId = useId();
  useEffect(() => setPhone(value), [value]);
  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);
  const changed = phone.replace(/\D/g, '') !== value.replace(/\D/g, '');
  const verified = !changed && Boolean(auth.user?.phone_verified_at)
    && phone.replace(/\D/g, '') === auth.user?.phone.replace(/\D/g, '');
  const needsVerification = changed || Boolean(phone && !verified);

  async function confirm(send: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      if (send) {
        await api('/api/auth/sms/send/', { method: 'POST', body: { phone, purpose: 'register' } });
        setSentPhone(phone);
        setCode('');
        setCooldown(60);
      } else {
        const user = await api<NonNullable<typeof auth.user>>('/api/auth/phone/change/', { method: 'POST', body: { phone, code } });
        auth.setAuthenticatedUser(user);
        onVerified(phone);
        setSentPhone('');
        setCode('');
      }
    } catch (cause) {
      setError(cause instanceof ApiError && cause.status === 429
        ? label('Слишком много попыток. Попробуйте позже.', 'Too many attempts. Please try again later.')
        : cause instanceof ApiError && cause.message === 'This phone number is already in use.'
          ? label('Этот номер уже используется.', 'This phone number is already in use.')
          : send
            ? label('Не удалось отправить код. Проверьте номер и повторите попытку.', 'Could not send the code. Check the number and try again.')
            : label('Неверный или просроченный код. Повторите попытку.', 'Invalid or expired code. Please try again.'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return <div className={`profile-phone-field${labelPosition === 'above' ? ' profile-phone-field--label-above' : ''}`} data-i18n-skip>
    {labelPosition === 'above' ? <label className="profile-phone-field__label" htmlFor={phoneId}><span>{label('Телефон', 'Phone')}</span></label> : null}
    <InternationalPhoneInput id={phoneId} density="compact" disabled={busy} label={labelPosition === 'inside' ? label('Телефон', 'Phone') : undefined} name="phone" value={phone}
      onChange={(next) => { setPhone(next); setSentPhone(''); setCode(''); setError(''); }} />
    {verified ? <span className="profile-phone-field__status" role="status">{label('Телефон подтверждён', 'Phone verified')}</span> : null}
    {needsVerification ? <>
      <small>{changed ? label('Новый номер сохранится после подтверждения по SMS.', 'The new number will be saved after SMS verification.') : label('Телефон не подтверждён', 'Phone not verified')}</small>
      <Button kind="tertiary" size="medium" disabled={busy || cooldown > 0 || !/^\+[1-9]\d{6,14}$/.test(phone)} onClick={() => void confirm(true)}>
        {cooldown > 0 ? label(`Повторить через ${cooldown} с`, `Resend in ${cooldown}s`) : label('Получить код', 'Send code')}
      </Button>
      {sentPhone === phone ? <div className="profile-phone-field__verification">
        <label>{label('Код из SMS', 'SMS code')}<input aria-label={label('Код из SMS', 'SMS code')} autoComplete="one-time-code" inputMode="numeric" maxLength={6} disabled={busy} value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} /></label>
        <Button kind="secondary" size="medium" disabled={busy || code.length !== 6} onClick={() => void confirm(false)}>{label('Подтвердить номер', 'Verify number')}</Button>
      </div> : null}
      {changed || sentPhone ? <Button kind="tertiary" size="medium" disabled={busy} onClick={() => { setPhone(value); setSentPhone(''); setCode(''); setError(''); }}>{label('Отменить', 'Cancel')}</Button> : null}
    </> : null}
    {error ? <p role="alert">{error}</p> : null}
  </div>;
}
